import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Link from "@material-ui/core/Link";
import { Link as RouterLink } from "react-router-dom";

import Button from "components/CustomButtons/Button";
import { useGlobal } from "GlobalContext";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  /**
   * Banner principal.
   *
   * Se mantiene fijo respecto al viewport y su posición vertical
   * la controla el layout mediante `bottomOffset`.
   *
   * Importante:
   * - NO animamos `bottom`
   * - así evitamos el efecto de caída/rebote al recalcular docking
   */
  root: {
    position: "fixed",
    zIndex: theme.zIndex.drawer - 2,
    backgroundColor: "#f9f9f9",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "0 -4px 18px rgba(0, 0, 0, 0.08)",
    padding: theme.spacing(2, 3),
    boxSizing: "border-box",
    transition: "box-shadow 120ms linear, opacity 120ms ease-out",

    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },

    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },

  /**
   * Variante visual cuando el banner ya está apoyado sobre el footer.
   * Solo cambia la sombra; no cambia la posición.
   */
  rootDocked: {
    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.06)",
  },

  /**
   * Contenido interno.
   *
   * En desktop: texto a la izquierda y acciones a la derecha.
   * En móvil: apilado.
   */
  content: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "flex-start",
      gap: theme.spacing(1.5),
    },
  },

  /**
   * Zona de texto.
   */
  textWrapper: {
    flex: 1,
    minWidth: 0,
  },

  text: {
    fontSize: "0.95rem",
    lineHeight: 1.5,
    color: "#000",

    [theme.breakpoints.down("sm")]: {
      fontSize: "0.9rem",
      textAlign: "center",
    },
  },

  privacyLink: {
    fontWeight: 500,
  },

  /**
   * Acciones.
   */
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      flexWrap: "wrap",
    },
  },
}));

/**
 * Opt-out defensivo en cliente.
 * - Si el usuario activa DNT o GPC, no disparamos analítica.
 * - El backend ya lo aplica, pero aquí evitamos incluso enviar la señal.
 */
function hasPrivacyOptOut() {
  try {
    const hasNavigator = typeof navigator !== "undefined";
    const hasWindow = typeof window !== "undefined";

    const dnt =
      (hasNavigator && navigator.doNotTrack) ||
      (hasWindow && window.doNotTrack) ||
      (hasNavigator && navigator.msDoNotTrack) ||
      null;

    const gpc = hasNavigator ? navigator.globalPrivacyControl : false;

    return String(dnt).trim() === "1" || gpc === true;
  } catch (_) {
    return false;
  }
}

/**
 * Señales mínimas de consentimiento para el script first-party `epalsafer-analytics.js`.
 *
 * IMPORTANTE:
 * - no crea cookies nuevas
 * - no crea identificadores persistentes
 * - no fingerprinting
 * - solo habilita el hit agregado cookieless con consentimiento explícito
 */
function applyAnalyticsConsentSignals() {
  try {
    window.__EPALSAFER_ANALYTICS_CONSENT__ = true;

    if (document && document.documentElement) {
      document.documentElement.setAttribute("data-analytics-consent", "true");
    }

    window.dispatchEvent(
      new CustomEvent("epal:analytics-consent", { detail: { consent: true } })
    );
  } catch (_) {
    // best-effort: nunca romper UX
  }
}

export default function CookiesNotice({
  bottomOffset = 0,
  isDockedToFooter = false,
  isReady = false,
  onHeightChange,
}) {
  const { t } = useTranslation("cookieNotice");
  const classes = useStyles();
  const { sharedValue, updateSharedValue } = useGlobal();

  /**
   * Ref al nodo raíz del banner.
   */
  const rootRef = React.useRef(null);

  /**
   * Posición horizontal y anchura sincronizadas con el mainPanel,
   * para no invadir visualmente la sidebar.
   */
  const [panelMetrics, setPanelMetrics] = React.useState({
    left: 0,
    width: 0,
  });

  /**
   * Flag local para no mostrar el banner hasta que ya conocemos
   * su left/width reales respecto al mainPanel.
   */
  const [panelMetricsReady, setPanelMetricsReady] = React.useState(false);

  /**
   * Si ya aceptó o denegó, no mostramos el aviso.
   */
  if (sharedValue.cookiesAccepted) return null;

  /**
   * Mide el contenedor padre real (mainPanel) para alinear el banner
   * exactamente con el área de contenido.
   */
  const syncWithParentPanel = React.useCallback(() => {
    const root = rootRef.current;
    if (!root || !root.parentElement) return false;

    const parentRect = root.parentElement.getBoundingClientRect();

    const nextMetrics = {
      left: Math.round(parentRect.left),
      width: Math.round(parentRect.width),
    };

    const isValid = nextMetrics.width > 0;

    setPanelMetrics((prev) => {
      if (
        prev.left === nextMetrics.left &&
        prev.width === nextMetrics.width
      ) {
        return prev;
      }

      return nextMetrics;
    });

    setPanelMetricsReady(isValid);
    return isValid;
  }, []);

  /**
   * Informa al layout de la altura real del banner.
   * Esto permite reservar espacio al final del contenido.
   */
  const notifyHeight = React.useCallback(() => {
    if (!rootRef.current || typeof onHeightChange !== "function") return;

    const nextHeight = Math.ceil(rootRef.current.getBoundingClientRect().height);
    if (nextHeight > 0) {
      onHeightChange(nextHeight);
    }
  }, [onHeightChange]);

  /**
   * Sincronización inicial antes de pintar visualmente el banner.
   * useLayoutEffect ayuda a evitar flashes de posición incorrecta.
   */
  React.useLayoutEffect(() => {
    syncWithParentPanel();
    notifyHeight();
  }, [syncWithParentPanel, notifyHeight]);

  /**
   * Re-sincroniza métricas y altura cuando cambian valores
   * que pueden afectar al layout real del banner.
   */
  React.useEffect(() => {
    syncWithParentPanel();
    notifyHeight();
  }, [
    syncWithParentPanel,
    notifyHeight,
    bottomOffset,
    isDockedToFooter,
    isReady,
  ]);

  /**
   * Observa cambios de tamaño tanto del panel padre como del propio banner.
   * Así reaccionamos a:
   * - cambios responsive
   * - cambio de ancho por sidebar mini
   * - cambios de contenido / wrapping del texto
   */
  React.useEffect(() => {
    const root = rootRef.current;
    const parent = root?.parentElement;

    if (!root) return;

    let resizeObserver = null;
    let rafId = null;

    const scheduleSync = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      rafId = window.requestAnimationFrame(() => {
        syncWithParentPanel();
        notifyHeight();
      });
    };

    scheduleSync();

    window.addEventListener("resize", scheduleSync);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        scheduleSync();
      });

      resizeObserver.observe(root);

      if (parent) {
        resizeObserver.observe(parent);
      }
    }

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }

      window.removeEventListener("resize", scheduleSync);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [syncWithParentPanel, notifyHeight]);

  const handleAccept = () => {
    // 1) Guardar preferencia
    updateSharedValue("cookiesAccepted", true);

    // 2) Respetar opt-out de privacidad
    if (hasPrivacyOptOut()) return;

    // 3) Señalizar consentimiento explícito para analítica cookieless
    applyAnalyticsConsentSignals();
  };

  const handleDeny = () => {
    updateSharedValue("cookiesAccepted", true);
  };

  /**
   * El banner solo debe hacerse visible cuando:
   * - el layout ya confirmó que está listo
   * - conocemos left/width reales del mainPanel
   *
   * Así evitamos:
   * - aparición tardía tras primer scroll
   * - salto horizontal inicial
   * - falsa animación de recolocación
   */
  const shouldBeVisible = isReady && panelMetricsReady;

  const rootStyle = {
    left: `${panelMetrics.left}px`,
    width: `${panelMetrics.width}px`,
    bottom: `${Math.max(0, bottomOffset)}px`,
    visibility: shouldBeVisible ? "visible" : "hidden",
    opacity: shouldBeVisible ? 1 : 0,
    pointerEvents: shouldBeVisible ? "auto" : "none",
  };

  return (
    <div
      ref={rootRef}
      className={`${classes.root} ${isDockedToFooter ? classes.rootDocked : ""}`}
      style={rootStyle}
      role="region"
      aria-label={t("cookies-title", { defaultValue: "Cookie notice" })}
    >
      <div className={classes.content}>
        <div className={classes.textWrapper}>
          <Typography className={classes.text}>
            {t("cookies-notice")}{" "}
            <Link
              component={RouterLink}
              to="/privacy"
              color="inherit"
              underline="always"
              className={classes.privacyLink}
            >
              {t("cookies-privacy")}
            </Link>
            .
          </Typography>
        </div>

        <div className={classes.actions}>
          <Button color="primary" size="sm" type="button" onClick={handleAccept}>
            {t("cookies-accept")}
          </Button>

          <Button color="rose" size="sm" type="button" onClick={handleDeny}>
            {t("cookies-deny")}
          </Button>
        </div>
      </div>
    </div>
  );
}

CookiesNotice.propTypes = {
  bottomOffset: PropTypes.number,
  isDockedToFooter: PropTypes.bool,
  isReady: PropTypes.bool,
  onHeightChange: PropTypes.func,
};